import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// ✅ Create and EXPORT the context
export const ProjectPermissionContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const ProjectPermissionProvider = ({ children, projectId }) => {
  const { user, token } = useAuth();
  const [projectRole, setProjectRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (projectId && user && token) {
      fetchProjectPermissions();
    }
  }, [projectId, user, token]);

  const fetchProjectPermissions = async () => {
    try {
      setLoading(true);

      // If user is admin, they have full access
      if (user.role === 'company_admin' || user.role === 'super_admin') {
        setProjectRole('admin');
        setPermissions({
          canView: true,
          canEdit: true,
          canDelete: true,
          canManageStaff: true,
          canViewBudget: true,
          canEditBudget: true,
          canExportData: true,
          canManageTasks: true,
          canEditProgress: true,
          canViewReports: true,
        });
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // For staff members, fetch their project assignment
      const response = await axios.get(
        `${API_URL}/project-staff/user/${user.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { projectRole: null, isActive: true }
        }
      );

      if (response.data.success) {
        const assignment = response.data.data.assignments.find(
          a => a.project.id === projectId
        );

        if (assignment) {
          setProjectRole(assignment.projectRole);
          setPermissions(getPermissionsForRole(assignment.projectRole, assignment.permissions));
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      }
    } catch (error) {
      console.error('Error fetching project permissions:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  // Define permissions based on project role
  const getPermissionsForRole = (role, customPermissions = {}) => {
    const rolePermissions = {
      project_manager: {
        canView: true,
        canEdit: true,
        canDelete: true,
        canManageStaff: false,
        canViewBudget: true,
        canEditBudget: true,
        canExportData: true,
        canManageTasks: true,
        canEditProgress: true,
        canViewReports: true,
      },
      site_engineer: {
        canView: true,
        canEdit: true,
        canDelete: false,
        canManageStaff: false,
        canViewBudget: true,
        canEditBudget: false,
        canExportData: true,
        canManageTasks: true,
        canEditProgress: true,
        canViewReports: true,
      },
      supervisor: {
        canView: true,
        canEdit: true,
        canDelete: false,
        canManageStaff: false,
        canViewBudget: false,
        canEditBudget: false,
        canExportData: false,
        canManageTasks: false,
        canEditProgress: true,
        canViewReports: true,
      },
      viewer: {
        canView: true,
        canEdit: false,
        canDelete: false,
        canManageStaff: false,
        canViewBudget: false,
        canEditBudget: false,
        canExportData: false,
        canManageTasks: false,
        canEditProgress: false,
        canViewReports: true,
      },
    };

    return {
      ...rolePermissions[role],
      ...customPermissions,
    };
  };

  const value = {
    projectRole,
    permissions,
    loading,
    hasAccess,
    isAdmin: user?.role === 'company_admin' || user?.role === 'super_admin',
    isProjectManager: projectRole === 'project_manager',
    isSiteEngineer: projectRole === 'site_engineer',
    isSupervisor: projectRole === 'supervisor',
    isViewer: projectRole === 'viewer',
  };

  return (
    <ProjectPermissionContext.Provider value={value}>
      {children}
    </ProjectPermissionContext.Provider>
  );
};

// ✅ This is the hook that's already exported
export const useProjectPermissions = () => {
  const context = useContext(ProjectPermissionContext);
  if (!context) {
    throw new Error('useProjectPermissions must be used within ProjectPermissionProvider');
  }
  return context;
};
